import React, { Component } from 'react';
import './ConfiguratorImage.css';

/**
 * v1.05: Helper to normalize a dependency field.
 * Accepts: null, undefined, a string, or an array of strings.
 * Always returns an array (possibly empty).
 */
function normalizeDependency(dep) {
    if (dep == null) return [];
    if (typeof dep === 'string') return [dep];
    if (Array.isArray(dep)) return dep;
    return [];
}

/**
 * v1.05: Build the filename suffix for a matte/overlay entry's dependencies
 * given the current configuration state.
 */
function buildDependencySuffix(deps, configuration) {
    let suffix = '';
    for (let i = 0; i < deps.length; i++) {
        suffix += '_' + configuration[deps[i]].value;
    }
    return suffix;
}

class ConfiguratorImage extends Component {

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            oldImages: [],
            images: [],
            imagesLoaded: 0,
            isTransitioning: false,
            hasLoadedOnce: false
        };

        this.transitionTimeout = null;

        this.handleImageLoaded = this.handleImageLoaded.bind(this);
        this.handleImageErrored = this.handleImageErrored.bind(this);
    }

    componentDidMount() {
        this.UNSAFE_componentWillReceiveProps(this.props);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }

        const normalizedBoat = (nextProps.boat || '').toLowerCase();
        var basePath = process.env.REACT_APP_IMAGE_HOST + "/" + normalizedBoat + "/" + nextProps.options.angle + "/";
        var images = [];


        if (this.props.options.legacy) {
            var legacyFilename = process.env.REACT_APP_IMAGE_HOST + "/" + normalizedBoat + "/" + nextProps.options.prefix + "_" + nextProps.options.angle;

            for (const key in nextProps.configuration) {
                if (nextProps.configuration.hasOwnProperty(key)) {
                    let element = nextProps.configuration[key].value;

                    if (nextProps.options.staticOptions[key] !== undefined) {
                        element = nextProps.options.staticOptions[key];
                    }

                    legacyFilename += "_" + element;
                }
            }

            legacyFilename += ".jpg";
            images.push({ url: legacyFilename, key: Math.random().toString() });
        } else {
            var filename = basePath + "base/" + nextProps.options.angle;

            for (const key in nextProps.configuration) {
                if (nextProps.configuration.hasOwnProperty(key)) {
                    let element = nextProps.configuration[key].value;

                    if (nextProps.options.staticOptions[key] !== undefined) {
                        element = nextProps.options.staticOptions[key];
                    }

                    filename += "_" + element;
                }
            }

            filename += ".jpg";
            images.push({ url: filename, key: Math.random().toString() });

            for (var i = 0; i < nextProps.options.mattes.length; i++) {
                var matte = nextProps.options.mattes[i];
                filename = basePath + "matte/" + nextProps.options.angle + "_";
                filename += matte.option;
                filename += "_" + nextProps.configuration[matte.option].value;

                // v1.05: Multi-dependency support
                var deps = normalizeDependency(matte.dependency);
                filename += buildDependencySuffix(deps, nextProps.configuration);

                filename += ".png";
                images.push({ url: filename, key: Math.random().toString() });
            }
        }

        // v1.05: Compute overlay URLs and pass to parent via callback
        var overlayUrls = [];
        if (nextProps.options.overlays && nextProps.options.overlays.length > 0 && !nextProps.options.legacy) {
            for (var oi = 0; oi < nextProps.options.overlays.length; oi++) {
                var overlay = nextProps.options.overlays[oi];
                var overlayFilename = basePath + "overlay/" + nextProps.options.angle + "_";
                overlayFilename += overlay.option;
                var overlayOptionValue = nextProps.configuration[overlay.option] !== undefined
                    ? nextProps.configuration[overlay.option].value
                    : nextProps.options.staticOptions[overlay.option];
                overlayFilename += "_" + overlayOptionValue;

                var overlayDeps = normalizeDependency(overlay.dependency);
                overlayFilename += buildDependencySuffix(overlayDeps, nextProps.configuration);

                overlayFilename += ".png";
                overlayUrls.push(overlayFilename);
            }
        }

        if (nextProps.onOverlayUrls) {
            nextProps.onOverlayUrls(overlayUrls);
        }

        var changed = false;

        for (i = 0; i < images.length; i++) {
            if (this.state.images[i] === undefined || this.state.images[i].url !== images[i].url) {
                changed = true;
                break;
            }
        }

        if (changed) {
            // On first load we don't want a crossfade; just show the initial images once loaded.
            if (!this.state.hasLoadedOnce) {
                this.setState({
                    loading: true,
                    oldImages: [],
                    images: images,
                    imagesLoaded: 0,
                    isTransitioning: false
                });
            } else {
                this.setState({
                    loading: true,
                    oldImages: this.state.images,
                    images: images,
                    imagesLoaded: 0,
                    isTransitioning: true
                });
            }
        }
    }

    componentWillUnmount() {
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
    }

    handleImageLoaded() {
        this.setState((prevState) => {
            const imagesLoaded = prevState.imagesLoaded + 1;
            const allLoaded = imagesLoaded >= prevState.images.length;

            const nextState = {
                imagesLoaded: imagesLoaded,
                loading: !allLoaded
            };

            if (allLoaded && !prevState.hasLoadedOnce) {
                nextState.hasLoadedOnce = true;
                nextState.isTransitioning = false;
                nextState.oldImages = [];
            }

            return nextState;
        }, () => {
            const { imagesLoaded, images, hasLoadedOnce, isTransitioning } = this.state;
            const allLoadedNow = imagesLoaded >= images.length;

            if (allLoadedNow && hasLoadedOnce && isTransitioning) {
                if (this.transitionTimeout) {
                    clearTimeout(this.transitionTimeout);
                }

                // Allow CSS opacity transition to complete before dropping old images.
                this.transitionTimeout = setTimeout(() => {
                    this.setState({
                        isTransitioning: false,
                        oldImages: []
                    });
                    this.transitionTimeout = null;
                }, 300);
            }
        });
    }

    handleImageErrored() {
        console.log("image failed to load");

        this.setState((prevState) => {
            const imagesLoaded = prevState.imagesLoaded + 1;
            const allLoaded = imagesLoaded >= prevState.images.length;

            const nextState = {
                imagesLoaded: imagesLoaded,
                loading: !allLoaded
            };

            if (allLoaded && !prevState.hasLoadedOnce) {
                nextState.hasLoadedOnce = true;
                nextState.isTransitioning = false;
                nextState.oldImages = [];
            }

            return nextState;
        }, () => {
            const { imagesLoaded, images, hasLoadedOnce, isTransitioning } = this.state;
            const allLoadedNow = imagesLoaded >= images.length;

            if (allLoadedNow && hasLoadedOnce && isTransitioning) {
                if (this.transitionTimeout) {
                    clearTimeout(this.transitionTimeout);
                }

                // Allow CSS opacity transition to complete before dropping old images.
                this.transitionTimeout = setTimeout(() => {
                    this.setState({
                        isTransitioning: false,
                        oldImages: []
                    });
                    this.transitionTimeout = null;
                }, 300);
            }
        });
    }

    render() {
        const { loading, isTransitioning, images, oldImages } = this.state;
        const showOldStack = isTransitioning && oldImages && oldImages.length > 0;
        const containerClassName = "imageStack" +
            (loading ? " loading" : "") +
            (isTransitioning ? " transitioning" : "");

        return (
            <div className={containerClassName}>

                <div className="imageStack-new">
                    {images.map(image => (
                        <img
                            key={image.key}
                            src={image.url}
                            onLoad={this.handleImageLoaded}
                            onError={this.handleImageErrored}
                            alt={this.props.options.boat} />
                    ))}
                </div>

                {showOldStack &&
                    <div className="imageStack-old">
                        {oldImages.map(image => (
                            <img
                                key={image.key}
                                src={image.url}
                                alt={this.props.options.boat} />
                        ))}
                    </div>
                }

                <div className="blocker" />

                <div className={loading ? "loader loading" : "loader"}><div className="spinner" /></div>
            </div>
        );
    }
}

export default ConfiguratorImage;



// WEBPACK FOOTER //
// ./src/ConfiguratorImage.js